import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import StatusBadge from "@/components/common/StatusBadge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApp } from "@/context/AppContext";
import { listAdminPayments, reconcileAdminPayment } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/formatters";
import { formatMoneyMinor, formatMoneyTotals } from "@/pages/overviewModel";

const PAGE_SIZE = 40;
const STATUSES = ["initialized", "pending", "paid", "trial", "failed", "canceled"];
const METHODS = [
  ["free_trial", "Free trial"],
  ["card", "Card"],
  ["bank", "Bank"],
  ["bank_transfer", "Bank transfer"],
  ["ussd", "USSD"],
  ["qr", "QR"],
  ["mobile_money", "Mobile money"],
  ["apple_pay", "Apple Pay"],
];

function PaymentsSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-label="Loading payments">
      <span className="sr-only">Loading payments</span>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[116px] rounded-xl" />)}
      </div>
      <Skeleton className="h-16 rounded-xl" />
      <div className="card-elev overflow-hidden rounded-xl">
        {Array.from({ length: 7 }, (_, index) => (
          <div key={index} className="flex h-[68px] items-center gap-5 border-b border-[#0E5B52]/8 px-4">
            <Skeleton className="h-4 w-28" /><Skeleton className="h-4 w-40" />
            <Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="ml-auto h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Payments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [cursorStack, setCursorStack] = useState([""]);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const debouncedSearch = search.trim();
  const canOperate = currentAdmin?.permissions?.includes("payments.manage");

  useEffect(() => {
    setCursorStack([""]);
    setPage(0);
  }, [debouncedSearch, method, status]);

  const filters = useMemo(() => ({
    cursor: cursorStack[page] || "",
    limit: PAGE_SIZE,
    method,
    query: debouncedSearch,
    status,
  }), [cursorStack, debouncedSearch, method, page, status]);

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery({
    queryKey: ["admin-payments", filters],
    queryFn: ({ signal }) => listAdminPayments(filters, signal),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: (paymentId) => reconcileAdminPayment(paymentId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-payments"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-overview"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-shops"] }),
      ]);
      setReconcileTarget(null);
      toast.success("Payment state verified with Squad");
    },
    onError: (mutationError) => toast.error(mutationError.message || "Squad verification failed"),
  });

  if (isLoading) {
    return (
      <div data-testid="payments-page">
        <PageHeader eyebrow="Finance" title="Payments" subtitle="Squad payments, internal free trials, immutable ledger activity and reconciliation state." />
        <PaymentsSkeleton />
      </div>
    );
  }

  const items = data?.items || [];
  const metrics = data?.metrics || {};
  const restricted = !currentAdmin?.permissions?.includes("financials.view");
  const money = (value) => restricted ? "Restricted" : formatMoneyTotals(value || []);
  const nextPage = () => {
    if (!data?.nextCursor) return;
    setCursorStack((current) => [...current.slice(0, page + 1), data.nextCursor]);
    setPage((current) => current + 1);
  };

  return (
    <div data-testid="payments-page">
      <PageHeader
        eyebrow="Finance"
        title="Payments"
        subtitle="Squad payments, internal free trials, immutable ledger activity and reconciliation state."
      />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Captured today" value={money(metrics.capturedToday)} icon={Wallet} accent="#A7F3D0" hint="Ledger-backed net captures" />
        <KpiCard label="Last 7 days" value={money(metrics.captured7d)} icon={CheckCircle2} accent="#BAE6FD" />
        <KpiCard label="Last 30 days" value={money(metrics.captured30d)} icon={ShieldCheck} accent="#D9F99D" />
        <KpiCard label="Pending" value={metrics.pending || 0} icon={Clock3} accent="#FDE047" hint={`${metrics.failed || 0} failed attempts`} />
      </div>

      <div className="card-elev mb-5 flex flex-col gap-3 rounded-xl p-4 md:flex-row">
        <PageSearchInput className="flex-1" value={search} onSearch={setSearch} placeholder="Search shop, reference or plan…" inputClassName="rounded-xl" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 w-full rounded-xl bg-white md:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((value) => <SelectItem key={value} value={value} className="capitalize">{value}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="h-10 w-full rounded-xl bg-white md:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All methods</SelectItem>
            {METHODS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="h-10 rounded-xl" disabled={isFetching} onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {isError && (
        <div className="mb-5 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
          <span>{error?.message || "Could not load payments."}</span>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      <div className={`card-elev overflow-hidden rounded-xl transition-opacity ${isFetching ? "opacity-70" : ""}`}>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F0F6F1]/60 hover:bg-[#F0F6F1]/60">
              <TableHead>Date</TableHead><TableHead>Shop</TableHead><TableHead>Plan</TableHead>
              <TableHead>Method</TableHead><TableHead>Status</TableHead>
              <TableHead>Reference</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-4">
                  <div className="flex h-44 items-center justify-center rounded-xl bg-[#F0F6F1]/60 text-center text-sm text-[#6B7280]">
                    No payment attempts match the current filters.
                  </div>
                </TableCell>
              </TableRow>
            ) : items.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{fmtDate(payment.createdAt)}</TableCell>
                <TableCell>
                  <button className="text-left font-semibold text-[#064E3B] hover:underline" onClick={() => navigate(`/shops/${payment.shopId}?tab=payments`)}>
                    {payment.shopName}
                  </button>
                </TableCell>
                <TableCell>{payment.planName}</TableCell>
                <TableCell className="capitalize">{payment.providerChannel?.replaceAll("_", " ") || payment.provider}</TableCell>
                <TableCell><StatusBadge status={payment.status} /></TableCell>
                <TableCell className="max-w-44 truncate font-mono text-xs" title={payment.providerReference}>{payment.providerReference || "—"}</TableCell>
                <TableCell className="text-right font-semibold text-[#064E3B]">
                  {payment.amountMinor == null ? "Restricted" : formatMoneyMinor(payment.amountMinor, payment.currencyCode)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    {canOperate && ["initialized", "pending"].includes(payment.status) && (
                      <Button size="sm" variant="outline" onClick={() => setReconcileTarget(payment)}>Reconcile</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setSelected(payment)}>View</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {(page > 0 || data?.nextCursor) && (
        <div className="mt-4 flex items-center justify-between text-xs text-[#4B5563]">
          <span>Page {page + 1} · 40 records per page</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((value) => value - 1)}><ArrowLeft className="mr-1 h-3.5 w-3.5" />Previous</Button>
            <Button size="sm" variant="outline" disabled={!data?.nextCursor} onClick={nextPage}>Next<ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}

      <Sheet open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Payment attempt</SheetTitle>
            <SheetDescription className="break-all">{selected?.providerReference}</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl bg-[#F0F6F1]/60 p-5">
                <div className="text-xs uppercase tracking-wider text-[#6B7280]">Amount</div>
                <div className="mt-1 text-2xl font-semibold text-[#064E3B]">
                  {selected.amountMinor == null ? "Restricted" : formatMoneyMinor(selected.amountMinor, selected.currencyCode)}
                </div>
                <div className="mt-3"><StatusBadge status={selected.status} /></div>
              </div>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-[#6B7280]">Created</dt><dd>{fmtDateTime(selected.createdAt)}</dd></div>
                <div><dt className="text-[#6B7280]">Paid</dt><dd>{selected.status === "trial" ? "Not applicable" : selected.paidAt ? fmtDateTime(selected.paidAt) : "Not paid"}</dd></div>
                <div><dt className="text-[#6B7280]">Provider</dt><dd className="capitalize">{selected.provider}</dd></div>
                <div><dt className="text-[#6B7280]">Ledger entries</dt><dd>{selected.ledgerEntryCount}</dd></div>
                <div><dt className="text-[#6B7280]">Verification attempts</dt><dd>{selected.reconcileAttemptCount}</dd></div>
                <div><dt className="text-[#6B7280]">Last verified</dt><dd>{selected.lastReconciledAt ? fmtDateTime(selected.lastReconciledAt) : "Never"}</dd></div>
              </dl>
              {selected.lastReconcileError && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="mb-1 flex items-center gap-2 font-semibold"><AlertCircle className="h-4 w-4" />Latest verification result</div>
                  {selected.lastReconcileError}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(reconcileTarget)} onOpenChange={(open) => { if (!open && !mutation.isPending) setReconcileTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verify this payment with Squad?</AlertDialogTitle>
            <AlertDialogDescription>
              Shopbook will query Squad using the stored reference. Access is activated only when the amount, currency, reference, and successful status all match.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                mutation.mutate(reconcileTarget?.id);
              }}
              className="bg-[#0E5B52] hover:bg-[#0A4640]"
            >
              Verify with Squad
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
